import { useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Hook to manage viewport height across different mobile browsers.
 * 
 * This hook addresses the "100vh" problem on mobile browsers where:
 * - iOS Safari has a dynamic address bar that changes viewport height
 * - Android Chrome has similar behavior
 * - The CSS 100vh doesn't always equal the visible viewport
 * 
 * It provides both CSS custom property updates and React state values.
 */
export function useViewportHeight() {
  const [viewportHeight, setViewportHeight] = useState<number>(
    typeof window !== 'undefined' ? window.innerHeight : 0
  );
  const [safeAreaTop, setSafeAreaTop] = useState<number>(0);
  const [safeAreaBottom, setSafeAreaBottom] = useState<number>(0);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState<boolean>(false);

  // Calculate and update viewport dimensions
  const updateViewportHeight = useCallback(() => {
    if (typeof window === 'undefined') return;

    // Use visualViewport if available (better for keyboard handling)
    const vh = window.visualViewport?.height ?? window.innerHeight;
    
    // Only update state if viewport height actually changed significantly (> 5px)
    // This prevents micro-updates from causing unnecessary re-renders
    setViewportHeight(prev => Math.abs(prev - vh) > 5 ? vh : prev);

    // Update CSS custom property for use in stylesheets
    document.documentElement.style.setProperty('--vh', `${vh * 0.01}px`);
    document.documentElement.style.setProperty('--app-height', `${vh}px`);

    // Detect if keyboard is likely visible:
    // 1. Viewport is significantly smaller than screen (keyboard taking space)
    // 2. AND an input/textarea is currently focused (user is actually typing)
    const screenHeight = window.screen.height;
    const heightRatio = vh / screenHeight;
    const viewportIsSmall = heightRatio < 0.75;
    
    // Check if an input element is focused
    const activeElement = document.activeElement;
    const isInputFocused = activeElement instanceof HTMLElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.isContentEditable
    );
    
    // Only consider keyboard open if BOTH conditions are true
    const keyboardVisible = viewportIsSmall && isInputFocused;
    
    // Only update keyboard state if it actually changed
    setIsKeyboardVisible(prev => {
      if (prev === keyboardVisible) return prev;
      
      // Toggle keyboard-open class on body for CSS-based adjustments
      if (keyboardVisible) {
        document.body.classList.add('keyboard-open');
      } else {
        document.body.classList.remove('keyboard-open');
      }
      return keyboardVisible;
    });
  }, []);

  // Get safe area insets from CSS environment variables
  const updateSafeAreaInsets = useCallback(() => {
    if (typeof window === 'undefined') return;

    // Create a temporary element to measure safe area insets
    const testElement = document.createElement('div');
    testElement.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
      visibility: hidden;
      padding-top: env(safe-area-inset-top, 0px);
      padding-bottom: env(safe-area-inset-bottom, 0px);
    `;
    document.body.appendChild(testElement);

    const computedStyle = window.getComputedStyle(testElement);
    const top = parseInt(computedStyle.paddingTop, 10) || 0;
    const bottom = parseInt(computedStyle.paddingBottom, 10) || 0;

    document.body.removeChild(testElement);

    setSafeAreaTop(top);
    setSafeAreaBottom(bottom);

    // Update CSS custom properties
    document.documentElement.style.setProperty('--safe-area-top', `${top}px`);
    document.documentElement.style.setProperty('--safe-area-bottom', `${bottom}px`);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Initial calculations
    updateViewportHeight();
    updateSafeAreaInsets();

    // Debounce resize handler to prevent excessive updates
    let resizeTimeout: ReturnType<typeof setTimeout>;
    const debouncedUpdate = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateViewportHeight();
        updateSafeAreaInsets();
      }, 50);
    };

    // Use visualViewport API if available (more reliable for mobile)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', debouncedUpdate);
      window.visualViewport.addEventListener('scroll', updateViewportHeight);
    }

    // Fallback to window resize
    window.addEventListener('resize', debouncedUpdate);
    window.addEventListener('orientationchange', () => {
      // Delay slightly after orientation change for accurate measurements
      setTimeout(() => {
        updateViewportHeight();
        updateSafeAreaInsets();
      }, 150);
    });

    // Handle focus events for keyboard detection
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        // Delay to allow keyboard animation
        setTimeout(updateViewportHeight, 100);
      }
    };

    const handleFocusOut = () => {
      setTimeout(updateViewportHeight, 100);
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      clearTimeout(resizeTimeout);
      
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', debouncedUpdate);
        window.visualViewport.removeEventListener('scroll', updateViewportHeight);
      }
      
      window.removeEventListener('resize', debouncedUpdate);
      window.removeEventListener('orientationchange', debouncedUpdate);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, [updateViewportHeight, updateSafeAreaInsets]);

  // Memoize return value to prevent unnecessary re-renders
  return useMemo(() => ({
    viewportHeight,
    safeAreaTop,
    safeAreaBottom,
    isKeyboardVisible,
    // Utility to get height for CSS-in-JS
    cssHeight: `${viewportHeight}px`,
    // Calculate available content height (minus safe areas)
    contentHeight: viewportHeight - safeAreaTop - safeAreaBottom,
  }), [viewportHeight, safeAreaTop, safeAreaBottom, isKeyboardVisible]);
}

/**
 * Initialize viewport height CSS custom properties.
 * Call this once at app startup (before React renders) for immediate styling.
 */
export function initViewportHeight(): void {
  if (typeof window === 'undefined') return;

  const vh = window.visualViewport?.height ?? window.innerHeight;
  document.documentElement.style.setProperty('--vh', `${vh * 0.01}px`);
  document.documentElement.style.setProperty('--app-height', `${vh}px`);
}

